import React, { useState, useEffect } from "react";

// Import transformation assets
import davidB_before from "../../Content 12 week challenge/real testimonials/David B before.jpg";
import davidB_after from "../../Content 12 week challenge/real testimonials/David B after.jpg";
import anthony_before from "../../Content 12 week challenge/real testimonials/Anthony Before 1.jpg";
import anthony_after from "../../Content 12 week challenge/real testimonials/Anthony After 1.jpg";
import chibs_before from "../../Content 12 week challenge/real testimonials/Chibs before.jpg";
import chibs_after from "../../Content 12 week challenge/real testimonials/Chibs after.jpg";
import davidA_before from "../../Content 12 week challenge/real testimonials/David A before.jpg";
import davidA_after from "../../Content 12 week challenge/real testimonials/David A after.jpg";
import henry_before from "../../Content 12 week challenge/real testimonials/Henry before.jpg";
import henry_after from "../../Content 12 week challenge/real testimonials/Henry after.jpg";
import jean_before from "../../Content 12 week challenge/real testimonials/Jean before.jpg";
import jean_after from "../../Content 12 week challenge/real testimonials/Jean after.jpg";
import joseph_before from "../../Content 12 week challenge/real testimonials/Joseph before.jpg";
import joseph_after from "../../Content 12 week challenge/real testimonials/Joseph after.jpg";
import saydul_before from "../../Content 12 week challenge/real testimonials/Saydul before.jpg";
import saydul_after from "../../Content 12 week challenge/real testimonials/Saydul after.jpg";
import jaylen_before from "../../Content 12 week challenge/real testimonials/Jaylen before.jpg";
import jaylen_after from "../../Content 12 week challenge/real testimonials/Jaylen after.jpg";

// Import physique/transformation dedicated shots
import chibeze_physique from "../../Content 12 week challenge/real testimonials/Physique/Chibeze physique.jpg";
import kimarni_physique from "../../Content 12 week challenge/real testimonials/Physique/Kimarni Physique.jpg";

// Video Review Assets (Reference paths)
import vsl_poster from "../../Content 12 week challenge/vsl_poster.png";

interface Transformation {
  name: string;
  role: string;
  weightLoss: string;
  profileImage: string;
  quote: string;
  beforeImage?: string;
  afterImage?: string;
  transformationImage?: string;
  videoUrl?: string;
  hoverEffect?: "zoom-in" | "zoom-out";
}

const TransformationCardComponent: React.FC<{
  person: Transformation;
}> = ({ person }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
  const isVimeo = person.videoUrl?.includes("vimeo.com");

  useEffect(() => {
    if (isVimeo && person.videoUrl) {
      let oembedUrl = `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(person.videoUrl)}`;

      // If the URL has a hash (unlisted), Vimeo oEmbed often prefers the /id/hash format
      if (person.videoUrl.includes("h=")) {
        const idMatch = person.videoUrl.match(/vimeo.com\/(\d+)/);
        const hashMatch = person.videoUrl.match(/[?&]h=([^&]+)/);
        if (idMatch && hashMatch) {
          const canonicalUrl = `https://vimeo.com/${idMatch[1]}/${hashMatch[1]}`;
          oembedUrl = `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(canonicalUrl)}`;
        }
      } else if (person.videoUrl.match(/vimeo.com\/\d+\/[a-zA-Z0-9]+/)) {
        // Already in /id/hash format
        oembedUrl = `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(person.videoUrl)}`;
      }

      fetch(oembedUrl)
        .then((res) => res.json())
        .then((data) => {
          if (data.thumbnail_url) {
            setThumbnailUrl(data.thumbnail_url);
          }
        })
        .catch((err) => console.error("Error fetching Vimeo thumbnail:", err));
    }
  }, [isVimeo, person.videoUrl]);

  const hoverClass =
    person.hoverEffect === "zoom-out"
      ? "scale-110 group-hover/img:scale-100"
      : "scale-100 group-hover/img:scale-110";

  return (
    <div className="bg-white dark:bg-surface-dark rounded-3xl p-6 border border-gray-100 dark:border-gray-800 shadow-xl hover:shadow-2xl transition-all duration-500 flex flex-col h-full group">
      <div className="flex items-center gap-4 mb-6">
        <div className="w-14 h-14 rounded-full bg-gradient-to-br from-primary to-blue-600 p-[2px] shrink-0">
          <div className="w-full h-full rounded-full bg-white dark:bg-surface-dark overflow-hidden">
            <img
              alt={person.name}
              className="w-full h-full object-cover"
              src={person.profileImage}
            />
          </div>
        </div>
        <div>
          <h4 className="text-lg font-bold text-slate-900 dark:text-white group-hover:text-primary transition-colors">
            {person.name}
          </h4>
          <p className="text-xs text-slate-500 dark:text-gray-400 font-medium tracking-wide">
            {person.role}
          </p>
        </div>
        <div className="ml-auto">
          <div className="px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20">
            <span className="text-sm font-bold text-emerald-500">
              {person.weightLoss}
            </span>
          </div>
        </div>
      </div>

      <div className="relative mb-6">
        <span className="absolute -left-2 -top-2 text-4xl text-primary opacity-20 font-serif">
          “
        </span>
        <p className="text-base text-slate-600 dark:text-gray-300 leading-relaxed italic relative z-10 px-2 lg:px-4">
          {person.quote}
        </p>
      </div>

      <div className="space-y-6">
        {/* Images Section */}
        {person.transformationImage ? (
          <div className="aspect-[4/3] rounded-2xl bg-gray-100 dark:bg-gray-900 relative overflow-hidden group/img cursor-zoom-in">
            <img
              alt={`${person.name} Transformation`}
              className={`w-full h-full object-cover transition-transform duration-700 ${hoverClass}`}
              src={person.transformationImage}
            />
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent p-4">
              <span className="bg-primary text-white text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-widest shadow-lg">
                Transformation Result
              </span>
            </div>
          </div>
        ) : (
          person.beforeImage &&
          person.afterImage && (
            <div className="flex flex-col gap-3">
              <div className="grid grid-cols-2 gap-3 relative">
                <div className="aspect-[3/4] rounded-2xl bg-gray-100 dark:bg-gray-900 relative overflow-hidden group/img">
                  <div className="absolute top-3 left-3 z-10 bg-black/70 backdrop-blur-md text-white text-[10px] font-black px-3 py-1 rounded-full uppercase tracking-tighter">
                    Before
                  </div>
                  <img
                    alt={`${person.name} Before`}
                    className={`w-full h-full object-cover opacity-90 transition-transform duration-700 ${hoverClass}`}
                    src={person.beforeImage}
                  />
                </div>
                <div className="aspect-[3/4] rounded-2xl bg-gray-100 dark:bg-gray-900 relative overflow-hidden group/img border-2 border-primary/30">
                  <div className="absolute top-3 left-3 z-10 bg-primary backdrop-blur-md text-white text-[10px] font-black px-3 py-1 rounded-full uppercase tracking-tighter shadow-lg">
                    After
                  </div>
                  <img
                    alt={`${person.name} After`}
                    className={`w-full h-full object-cover transition-transform duration-700 ${hoverClass}`}
                    src={person.afterImage}
                  />
                </div>
              </div>
            </div>
          )
        )}

        {/* Video Section */}
        {person.videoUrl && (
          <div
            className="relative aspect-video rounded-2xl bg-black overflow-hidden group/video cursor-pointer border border-gray-100/10 dark:border-white/10"
            onClick={() => !isPlaying && setIsPlaying(true)}
          >
            {isVimeo ? (
              <div className="w-full h-full">
                {isPlaying ? (
                  <iframe
                    src={(() => {
                      const baseUrl = person
                        .videoUrl!.replace(
                          "vimeo.com",
                          "player.vimeo.com/video",
                        )
                        .replace(/\/\d+\/\w+/, (match) =>
                          match.replace(/\//g, (c, i) =>
                            i === 0 ? "/" : "?h=",
                          ),
                        ); // Handle /id/hash -> /id?h=hash

                      // Ensure we don't double up on ? or &
                      const separator = baseUrl.includes("?") ? "&" : "?";
                      return `${baseUrl}${separator}autoplay=1&badge=0&autopause=0&title=0&byline=0&portrait=0&player_id=0&app_id=58479`;
                    })()}
                    frameBorder="0"
                    allow="autoplay; fullscreen; picture-in-picture"
                    style={{ width: "100%", height: "100%" }}
                    title={person.name}
                  ></iframe>
                ) : (
                  <>
                    {thumbnailUrl ? (
                      <img
                        src={thumbnailUrl}
                        alt={`${person.name} Review Thumbnail`}
                        className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover/video:opacity-80 transition-all duration-500"
                      />
                    ) : (
                      <div className="absolute inset-0 bg-slate-900 group-hover/video:bg-slate-800 transition-colors" />
                    )}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-16 h-16 rounded-full bg-primary/90 flex items-center justify-center shadow-2xl transform group-hover/video:scale-110 transition-all duration-300">
                        <span className="material-symbols-outlined text-white text-3xl pl-1 font-extrabold">
                          play_arrow
                        </span>
                      </div>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <>
                {isPlaying ? (
                  <video
                    className="w-full h-full"
                    src={person.videoUrl}
                    controls
                    autoPlay
                  />
                ) : (
                  <>
                    <video
                      className="w-full h-full object-cover opacity-60 group-hover/video:opacity-80 transition-opacity"
                      src={person.videoUrl}
                    />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-16 h-16 rounded-full bg-primary/90 flex items-center justify-center shadow-2xl transform group-hover/video:scale-110 transition-all duration-300">
                        <span className="material-symbols-outlined text-white text-3xl pl-1 font-extrabold">
                          play_arrow
                        </span>
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
            {!isPlaying && (
              <div className="absolute top-4 left-4 bg-primary px-4 py-1.5 rounded-full text-[10px] font-black text-white uppercase tracking-[0.1em] z-10 shadow-xl border border-white/20">
                Video Review
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ⚡ Bolt: Wrap child component with React.memo to prevent unnecessary re-renders when parent states change (e.g. theme toggle, showAllTransformations).
// Expected impact: Reduces component tree reconciliation and re-rendering of the entire list.
const TransformationCard = React.memo(TransformationCardComponent);


// ⚡ Bolt: Hoisted static data and strings outside the component to ensure stable references across renders.
// Expected impact: Prevents redeclaration of large arrays and objects on every render cycle, working perfectly with React.memo() on child components.
const transformations: Transformation[] = [
    {
      name: "David B.",
      role: "Project Manager, Age 37",
      weightLoss: "+6kg muscle",
      profileImage:
        "https://lh3.googleusercontent.com/aida-public/AB6AXuBMCA0YDKITF72_bFxPAdvpbeMXe2VN2OViPi92F8KpUa2mYwrw2Jmzpo4CB1sXnTIMBOB9uJgevSIqWf22q677sar0mJu3sO_ppa4b_lygVIOdsV-4isO4Lwv7bdHpF1gR_QuCMxZ_HzSpQ4d9sy35ayCxdZt48RbVxEod7MohkSZkrsASOCkbNAvtAqqRfZr-XuhzfgFtOa99gFeVMNIKb9cAuTTueY_BStHfR-8g9HNr-kpxmY3aKfopP89femfDaE-RpdMrQOw",
      quote:
        "I was fit before, but this is a different level of fitness. I feel stronger, leaner, and more capable. This is the leanest I've ever been. Seeing the physical change was surreal — it completely changed how I see my body.",
      beforeImage: davidB_before,
      afterImage: davidB_after,
      videoUrl: "https://vimeo.com/1157938811?h=6c4dd48694",
    },
    {
      name: "Anthony",
      role: "Financial Specialist, Age 30",
      weightLoss: "+10kg muscle",
      profileImage:
        "https://lh3.googleusercontent.com/aida-public/AB6AXuBMCA0YDKITF72_bFxPAdvpbeMXe2VN2OViPi92F8KpUa2mYwrw2Jmzpo4CB1sXnTIMBOB9uJgevSIqWf22q677sar0mJu3sO_ppa4b_lygVIOdsV-4isO4Lwv7bdHpF1gR_QuCMxZ_HzSpQ4d9sy35ayCxdZt48RbVxEod7MohkSZkrsASOCkbNAvtAqqRfZr-XuhzfgFtOa99gFeVMNIKb9cAuTTueY_BStHfR-8g9HNr-kpxmY3aKfopP89femfDaE-RpdMrQOw",
      quote:
        "Before this, I prioritised everything except my health. Once I saw how quickly my body changed, it pushed me to stay consistent. The accountability kept me locked in, and the benefits carried over into other areas of my life.",
      beforeImage: anthony_before,
      afterImage: anthony_after,
      videoUrl: "https://vimeo.com/1157939246",
    },
    {
      name: "Saydul",
      role: "Cybersecurity Consultant, Age 27",
      weightLoss: "-10kg fat",
      profileImage:
        "https://lh3.googleusercontent.com/aida-public/AB6AXuBMCA0YDKITF72_bFxPAdvpbeMXe2VN2OViPi92F8KpUa2mYwrw2Jmzpo4CB1sXnTIMBOB9uJgevSIqWf22q677sar0mJu3sO_ppa4b_lygVIOdsV-4isO4Lwv7bdHpF1gR_QuCMxZ_HzSpQ4d9sy35ayCxdZt48RbVxEod7MohkSZkrsASOCkbNAvtAqqRfZr-XuhzfgFtOa99gFeVMNIKb9cAuTTueY_BStHfR-8g9HNr-kpxmY3aKfopP89femfDaE-RpdMrQOw",
      quote:
        "I set a 6-month fat loss goal and hit it in 12 weeks. The structure removed decision fatigue, and the accountability made consistency non-negotiable. This was the first time a program actually stuck.",
      beforeImage: saydul_before,
      afterImage: saydul_after,
    },
    {
      name: "Jaylen",
      role: "Athlete, Age 27",
      weightLoss: "-10kg fat",
      profileImage:
        "https://lh3.googleusercontent.com/aida-public/AB6AXuBMCA0YDKITF72_bFxPAdvpbeMXe2VN2OViPi92F8KpUa2mYwrw2Jmzpo4CB1sXnTIMBOB9uJgevSIqWf22q677sar0mJu3sO_ppa4b_lygVIOdsV-4isO4Lwv7bdHpF1gR_QuCMxZ_HzSpQ4d9sy35ayCxdZt48RbVxEod7MohkSZkrsASOCkbNAvtAqqRfZr-XuhzfgFtOa99gFeVMNIKb9cAuTTueY_BStHfR-8g9HNr-kpxmY3aKfopP89femfDaE-RpdMrQOw",
      quote:
        "I planned to lose 10kg in six months and did it in three. More than the fat loss, it pulled me out of a slump. My numbers are better than my college days.",
      beforeImage: jaylen_before,
      afterImage: jaylen_after,
      videoUrl: "https://vimeo.com/1157940926",
    },
    {
      name: "Henry",
      role: "Game Developer, Age 25",
      weightLoss: "+14kg muscle",
      profileImage:
        "https://lh3.googleusercontent.com/aida-public/AB6AXuBMCA0YDKITF72_bFxPAdvpbeMXe2VN2OViPi92F8KpUa2mYwrw2Jmzpo4CB1sXnTIMBOB9uJgevSIqWf22q677sar0mJu3sO_ppa4b_lygVIOdsV-4isO4Lwv7bdHpF1gR_QuCMxZ_HzSpQ4d9sy35ayCxdZt48RbVxEod7MohkSZkrsASOCkbNAvtAqqRfZr-XuhzfgFtOa99gFeVMNIKb9cAuTTueY_BStHfR-8g9HNr-kpxmY3aKfopP89femfDaE-RpdMrQOw",
      quote:
        "I genuinely didn't think my body could change this much. I'd been stuck for years, training but not progressing. The results came fast. This is the strongest and most confident I've ever been.",
      beforeImage: henry_before,
      afterImage: henry_after,
    },
    {
      name: "Joseph",
      role: "Software Engineer, Age 26",
      weightLoss: "+9kg muscle",
      profileImage:
        "https://lh3.googleusercontent.com/aida-public/AB6AXuBMCA0YDKITF72_bFxPAdvpbeMXe2VN2OViPi92F8KpUa2mYwrw2Jmzpo4CB1sXnTIMBOB9uJgevSIqWf22q677sar0mJu3sO_ppa4b_lygVIOdsV-4isO4Lwv7bdHpF1gR_QuCMxZ_HzSpQ4d9sy35ayCxdZt48RbVxEod7MohkSZkrsASOCkbNAvtAqqRfZr-XuhzfgFtOa99gFeVMNIKb9cAuTTueY_BStHfR-8g9HNr-kpxmY3aKfopP89femfDaE-RpdMrQOw",
      quote:
        "What changed everything was having a clear system to follow. No guessing, no wasted sessions. I finally built muscle properly and stayed lean while doing it.",
      beforeImage: joseph_before,
      afterImage: joseph_after,
    },
    {
      name: "Chibeze",
      role: "Athlete, Age 25",
      weightLoss: "+5kg muscle",
      profileImage:
        "https://lh3.googleusercontent.com/aida-public/AB6AXuBMCA0YDKITF72_bFxPAdvpbeMXe2VN2OViPi92F8KpUa2mYwrw2Jmzpo4CB1sXnTIMBOB9uJgevSIqWf22q677sar0mJu3sO_ppa4b_lygVIOdsV-4isO4Lwv7bdHpF1gR_QuCMxZ_HzSpQ4d9sy35ayCxdZt48RbVxEod7MohkSZkrsASOCkbNAvtAqqRfZr-XuhzfgFtOa99gFeVMNIKb9cAuTTueY_BStHfR-8g9HNr-kpxmY3aKfopP89femfDaE-RpdMrQOw",
      quote:
        "I thought I was already training hard. Turns out I wasn't training effectively. This program exposed gaps in my discipline and fixed them. Strength, structure, and consistency all went up.",
      beforeImage: chibs_before,
      afterImage: chibs_after,
      transformationImage: chibeze_physique,
    },
    {
      name: "Asad",
      role: "Lawyer, Age 29",
      weightLoss: "+4kg muscle",
      profileImage:
        "https://lh3.googleusercontent.com/aida-public/AB6AXuBMCA0YDKITF72_bFxPAdvpbeMXe2VN2OViPi92F8KpUa2mYwrw2Jmzpo4CB1sXnTIMBOB9uJgevSIqWf22q677sar0mJu3sO_ppa4b_lygVIOdsV-4isO4Lwv7bdHpF1gR_QuCMxZ_HzSpQ4d9sy35ayCxdZt48RbVxEod7MohkSZkrsASOCkbNAvtAqqRfZr-XuhzfgFtOa99gFeVMNIKb9cAuTTueY_BStHfR-8g9HNr-kpxmY3aKfopP89femfDaE-RpdMrQOw",
      quote:
        "I didn’t expect this level of progress in just 8 weeks. Before, it was hard to measure if I was improving. The structure and constant adjustments exposed weak areas and fixed them.",
      videoUrl: "https://vimeo.com/1157940314",
    },
    {
      name: "Jean",
      role: "Business Professional, Age 25",
      weightLoss: "-4kg fat",
      profileImage:
        "https://lh3.googleusercontent.com/aida-public/AB6AXuBMCA0YDKITF72_bFxPAdvpbeMXe2VN2OViPi92F8KpUa2mYwrw2Jmzpo4CB1sXnTIMBOB9uJgevSIqWf22q677sar0mJu3sO_ppa4b_lygVIOdsV-4isO4Lwv7bdHpF1gR_QuCMxZ_HzSpQ4d9sy35ayCxdZt48RbVxEod7MohkSZkrsASOCkbNAvtAqqRfZr-XuhzfgFtOa99gFeVMNIKb9cAuTTueY_BStHfR-8g9HNr-kpxmY3aKfopP89femfDaE-RpdMrQOw",
      quote:
        "Before this, I was inconsistent and unfocused with my health. The structure made everything simple — I knew exactly what to do and when.",
      beforeImage: jean_before,
      afterImage: jean_after,
    },
    {
      name: "Jair",
      role: "Logistics Specialist, Age 26",
      weightLoss: "-10kg fat",
      profileImage:
        "https://lh3.googleusercontent.com/aida-public/AB6AXuBMCA0YDKITF72_bFxPAdvpbeMXe2VN2OViPi92F8KpUa2mYwrw2Jmzpo4CB1sXnTIMBOB9uJgevSIqWf22q677sar0mJu3sO_ppa4b_lygVIOdsV-4isO4Lwv7bdHpF1gR_QuCMxZ_HzSpQ4d9sy35ayCxdZt48RbVxEod7MohkSZkrsASOCkbNAvtAqqRfZr-XuhzfgFtOa99gFeVMNIKb9cAuTTueY_BStHfR-8g9HNr-kpxmY3aKfopP89femfDaE-RpdMrQOw",
      quote:
        "Consistency changed everything. I lost fat around my midsection, my strength improved massively, and I feel confident in what I wear again.",
      videoUrl: "https://vimeo.com/1157939842",
    },
    {
      name: "Kimarni",
      role: "Athlete, Age 27",
      weightLoss: "Elite Physique",
      profileImage:
        "https://lh3.googleusercontent.com/aida-public/AB6AXuBMCA0YDKITF72_bFxPAdvpbeMXe2VN2OViPi92F8KpUa2mYwrw2Jmzpo4CB1sXnTIMBOB9uJgevSIqWf22q677sar0mJu3sO_ppa4b_lygVIOdsV-4isO4Lwv7bdHpF1gR_QuCMxZ_HzSpQ4d9sy35ayCxdZt48RbVxEod7MohkSZkrsASOCkbNAvtAqqRfZr-XuhzfgFtOa99gFeVMNIKb9cAuTTueY_BStHfR-8g9HNr-kpxmY3aKfopP89femfDaE-RpdMrQOw",
      quote:
        "As an athlete, I thought I was already disciplined. This program exposed gaps in my consistency and tightened everything up.",
      transformationImage: kimarni_physique,
    },
    {
      name: "David A.",
      role: "Trader, Age 24",
      weightLoss: "+5kg muscle",
      profileImage:
        "https://lh3.googleusercontent.com/aida-public/AB6AXuBMCA0YDKITF72_bFxPAdvpbeMXe2VN2OViPi92F8KpUa2mYwrw2Jmzpo4CB1sXnTIMBOB9uJgevSIqWf22q677sar0mJu3sO_ppa4b_lygVIOdsV-4isO4Lwv7bdHpF1gR_QuCMxZ_HzSpQ4d9sy35ayCxdZt48RbVxEod7MohkSZkrsASOCkbNAvtAqqRfZr-XuhzfgFtOa99gFeVMNIKb9cAuTTueY_BStHfR-8g9HNr-kpxmY3aKfopP89femfDaE-RpdMrQOw",
      quote:
        "I’d trained before, but never with this level of focus. Everything was structured, tracked, and adjusted. I saw noticeable upper-body growth within the first couple of months.",
      beforeImage: davidA_before,
      afterImage: davidA_after,
      hoverEffect: "zoom-out",
    },
    {
      name: "Rene",
      role: "IT Systems Consultant, Age 25",
      weightLoss: "+3kg muscle",
      profileImage:
        "https://lh3.googleusercontent.com/aida-public/AB6AXuBMCA0YDKITF72_bFxPAdvpbeMXe2VN2OViPi92F8KpUa2mYwrw2Jmzpo4CB1sXnTIMBOB9uJgevSIqWf22q677sar0mJu3sO_ppa4b_lygVIOdsV-4isO4Lwv7bdHpF1gR_QuCMxZ_HzSpQ4d9sy35ayCxdZt48RbVxEod7MohkSZkrsASOCkbNAvtAqqRfZr-XuhzfgFtOa99gFeVMNIKb9cAuTTueY_BStHfR-8g9HNr-kpxmY3aKfopP89femfDaE-RpdMrQOw",
      quote:
        "The biggest change was mental. I stopped seeing training as something I had to do and started seeing it as something I wanted to do.",
      videoUrl: "https://vimeo.com/1157940607",
    },
  ];

  var buttonSubText =
    "Your application starts with a short call to make sure we're a good fit.";
  const calendlyLink = "https://calendly.com/mdcwinners/action-plan-call";

const TwelveWeekChallenge: React.FC = () => {
  // Theme state: defaults to 'dark'
  const [theme, setTheme] = useState<"dark" | "light">(() => {
    if (typeof window !== "undefined") {
      const savedTheme = localStorage.getItem("theme");
      // Default to dark if no saved preference
      return (savedTheme as "dark" | "light") || "dark";
    }
    return "dark";
  });

  // Apply theme class to document
  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove("light", "dark");
    root.classList.add(theme);
    localStorage.setItem("theme", theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme((prev) => (prev === "dark" ? "light" : "dark"));
  };

  const trackPixelEvent = (
    eventName: string,
    params?: Record<string, unknown>,
  ) => {
    if (typeof window !== "undefined" && typeof window.fbq === "function") {
      window.fbq("trackCustom", eventName, params);
    }
  };

  const [showAllTestimonials, setShowAllTestimonials] = useState(false);
  const [isVslPlaying, setIsVslPlaying] = useState(false);

  const testimonials = [
    {
      quote:
        "The biggest shift wasn't just physical. My energy at work doubled. I stopped crashing at 3 PM and started leading with more clarity.",
      author: "Liam (Marketing Director), 31",
      rating: 5,
    },
    {
      quote:
        "I'd tried every app and diet. This was the first time someone actually looked at my life and built a plan that fit into my 60-hour work weeks.",
      author: "Marcus (Accountant), 28",
      rating: 4.5,
    },
    {
      quote:
        "The accountability kept me on track when I usually would have quit. 12 weeks later, I'm at a lower body fat than my early 20s.",
      author: "Nathan (Design Lead), 34",
      rating: 5,
    },
    {
      quote:
        "Structured, data-driven, and effective. As someone who likes logic, the science behind the training finally made sense to me.",
      author: "Chris (Software Architect), 26",
      rating: 5,
    },
    {
      quote:
        "Maintaining consistency while traveling was my biggest hurdle. We found a way to make it work in hotel gyms across three continents.",
      author: "James (Commercial Pilot), 42",
      rating: 5,
    },
    {
      quote:
        "I lost 12kg of fat but gained a completely different level of discipline. My wife says I'm a different man entirely.",
      author: "Sam (Sales VP), 38",
      rating: 4.5,
    },
    {
      quote:
        "The midsection fat I've had for years is finally gone. I feel more confident in a suit and even more confident without one.",
      author: "Ben (Operations), 29",
      rating: 5,
    },
    {
      quote:
        "Look, I’ll be honest, I was skeptical at first, right? But the structure... it just works. I didn't think I could look like this while still doing my job and everything else. It's just... I'm a believer now.",
      author: "Albert (Sales Person), 32",
      rating: 5,
    },
  ];

  const [showAllTransformations, setShowAllTransformations] = useState(false);
  const handleCtaClick = (location: string) => {
    trackPixelEvent("start_transformation_click", { location });
    window.open(calendlyLink, "_blank");
  };

  const handleHeroVideoClick = () => {
    trackPixelEvent("hero_video_play");
    setIsVslPlaying(true);
  };

  const handleTransformationsToggle = () => {
    const next = !showAllTransformations;
    setShowAllTransformations(next);
    trackPixelEvent("transformations_toggle", {
      action: next ? "expand" : "collapse",
    });
  };

  const handleTestimonialsToggle = () => {
    const next = !showAllTestimonials;
    setShowAllTestimonials(next);
    trackPixelEvent("testimonials_toggle", {
      action: next ? "expand" : "collapse",
    });
  };

  return (
    <div className="bg-background-light dark:bg-background-dark text-slate-900 dark:text-white font-display overflow-x-hidden antialiased selection:bg-primary selection:text-white min-h-screen transition-colors duration-300">
      {/* Changed max-w-md to max-w-7xl and adjusted responsive behaviors */}
      <div className="relative flex h-auto min-h-screen w-full flex-col max-w-7xl mx-auto shadow-2xl bg-background-light dark:bg-background-dark">
        <header className="sticky top-0 z-50 flex items-center bg-background-light/95 dark:bg-background-dark/95 backdrop-blur-md p-4 pb-2 justify-between border-b border-gray-200 dark:border-gray-800">
          <div className="flex items-center w-full relative justify-between">
            <div className="flex items-center gap-2">
              <span className="material-symbols-outlined text-primary text-3xl">
                fitness_center
              </span>
              <h2 className="text-slate-900 dark:text-white text-lg font-extrabold leading-tight tracking-[-0.015em] uppercase hidden sm:block">
                Chibs Olympia
              </h2>
            </div>

            <h2 className="text-slate-900 dark:text-white text-lg font-extrabold leading-tight tracking-[-0.015em] text-center uppercase sm:hidden absolute left-1/2 -translate-x-1/2">
              Chibs Olympia
            </h2>

            <button
              onClick={toggleTheme}
              className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-800 transition-colors z- relative"
              aria-label="Toggle theme"
            >
              <span className="material-symbols-outlined text-slate-600 dark:text-gray-300">
                {theme === "dark" ? "light_mode" : "dark_mode"}
              </span>
            </button>
          </div>
        </header>

        {/* Hero Section - Grid on desktop */}
        <section className="flex flex-col lg:grid lg:grid-cols-2 lg:gap-12 lg:items-center pt-8 pb-4 lg:py-20 px-5 max-w-6xl mx-auto w-full">
          <div className="text-center lg:text-left">
            <div className="inline-flex items-center justify-center px-3 py-1 rounded-full bg-primary/10 border border-primary/20 mb-4 text-center">
              <span className="w-2 h-2 rounded-full bg-primary mr-2 animate-pulse"></span>
              <span className="text-xs font-bold text-primary uppercase tracking-wider">
                NEW COHORT OPEN - 8 SLOTS LEFT OUT OF 10
              </span>
            </div>
            <h1 className="text-slate-900 dark:text-white text-[32px] lg:text-5xl font-extrabold leading-[1.1] mb-6 tracking-tight">
              Transform your physique{" "}
              <span className="text-primary bg-clip-text text-transparent bg-gradient-to-r from-primary to-blue-600">
                in 12 weeks.
              </span>
            </h1>
            <h2 className="text-slate-600 dark:text-gray-300 text-lg lg:text-xl font-medium leading-relaxed mb-8 px-2 lg:px-0 max-w-xl mx-auto lg:mx-0">
              Stop renting your results. Own them. This is the comprehensive
              system for men who are ready to master their body and mind for
              life.
            </h2>

            <div className="hidden lg:block">
              <div className="max-w-sm mx-auto lg:mx-0">
                <div className="relative group">
                  <div className="absolute -inset-1 bg-gradient-to-r from-primary to-blue-600 rounded-xl blur-xl opacity-25 group-hover:opacity-60 transition duration-1000 group-hover:duration-200"></div>
                  <button
                    onClick={() => handleCtaClick("hero")}
                    className="relative w-full flex flex-col items-center justify-center bg-gradient-to-r from-primary to-blue-600 hover:to-primary-dark transition-all duration-200 rounded-xl py-4 px-10 shadow-lg shadow-primary/25 mx-auto overflow-hidden"
                  >
                    <div className="absolute top-0 -left-full w-full h-full bg-gradient-to-r from-transparent via-white/20 to-transparent skew-x-[-30deg] animate-[shimmer_2.5s_infinite]"></div>
                    <div className="flex items-center gap-2 text-white text-lg font-bold leading-none tracking-wide z-10">
                      <span>Start Your Transformation</span>
                      <span className="material-symbols-outlined text-xl">
                        arrow_forward
                      </span>
                    </div>
                  </button>
                </div>
                <p className="text-[11px] text-slate-500 dark:text-gray-400 mt-4 font-medium px-4 leading-tight w-full text-center lg:text-left opacity-70">
                  {buttonSubText}
                </p>
              </div>
            </div>
          </div>

          <div className="mt-8 lg:mt-0 space-y-8">
            {/* Video Section */}
            <div className="bg-surface-dark w-full aspect-video relative flex items-center justify-center group cursor-pointer overflow-hidden border-y border-gray-800 lg:rounded-2xl lg:shadow-2xl">
              {!isVslPlaying ? (
                <div
                  className="absolute inset-0 w-full h-full cursor-pointer"
                  onClick={handleHeroVideoClick}
                >
                  <img
                    src={vsl_poster}
                    alt="Video Thumbnail"
                    className="w-full h-full object-cover opacity-60 group-hover:opacity-80 transition-opacity"
                  />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-20 h-20 rounded-full bg-primary/90 flex items-center justify-center shadow-2xl transform group-hover:scale-110 transition-all duration-300">
                      <span className="material-symbols-outlined text-white text-4xl pl-1 font-bold">
                        play_arrow
                      </span>
                    </div>
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-background-dark via-transparent to-transparent opacity-40 pointer-events-none"></div>
                </div>
              ) : (
                <div className="absolute inset-0 w-full h-full">
                  <iframe
                    src="https://player.vimeo.com/video/1157930231?h=ca87320b54&amp;badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479&amp;autoplay=1"
                    frameBorder="0"
                    allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                    }}
                    title="How we work"
                  ></iframe>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Repeating CTA for mobile mostly, or secondary for desktop */}
        <section className="px-5 pb-16 border-b border-gray-200 dark:border-gray-800 lg:hidden flex flex-col items-center">
          <div className="w-full max-w-sm">
            <div className="relative group">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-primary to-blue-600 rounded-xl blur-xl opacity-20"></div>
              <button
                onClick={() => handleCtaClick("mobile")}
                className="relative w-full flex flex-col items-center justify-center bg-gradient-to-r from-primary to-blue-600 hover:to-primary-dark transition-all duration-200 rounded-xl py-4 px-10 shadow-lg shadow-primary/25 mx-auto overflow-hidden"
              >
                <div className="absolute top-0 -left-full w-full h-full bg-gradient-to-r from-transparent via-white/20 to-transparent skew-x-[-30deg] animate-[shimmer_2.5s_infinite]"></div>
                <div className="flex items-center gap-2 text-white text-lg font-bold leading-none tracking-wide z-10">
                  <span>Start Your Transformation</span>
                  <span className="material-symbols-outlined text-xl">
                    arrow_forward
                  </span>
                </div>
              </button>
            </div>
            <p className="text-center text-[11px] text-slate-500 dark:text-gray-400 mt-3 font-medium px-4 leading-tight opacity-80">
              {buttonSubText}
            </p>
          </div>
        </section>

        <section className="px-5 py-12 bg-white dark:bg-surface-dark/30">
          <div className="flex flex-col items-center mb-12">
            <div className="flex items-center gap-1 mb-2">
              {[...Array(5)].map((_, i) => (
                <span
                  key={i}
                  className="material-symbols-outlined text-yellow-500 text-xl"
                >
                  star
                </span>
              ))}
            </div>
            <h3 className="text-2xl lg:text-3xl font-bold text-center text-slate-900 dark:text-white">
              Relatable Men. Real Transformation.
            </h3>
            <p className="text-sm lg:text-base text-slate-500 dark:text-gray-400 text-center mt-1">
              Built for men with careers, families, and busy lives.
            </p>
          </div>

          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              {transformations
                .slice(0, showAllTransformations ? undefined : 6)
                .map((person, i) => (
                  <TransformationCard key={i} person={person} />
                ))}
            </div>

            {transformations.length > 6 && (
              <div className="flex justify-center mb-12">
                <button
                  onClick={handleTransformationsToggle}
                  className="text-sm font-bold text-primary hover:text-primary-dark transition-colors flex items-center gap-1"
                >
                  {showAllTransformations ? "See Less" : "See More"}
                  <span
                    className={`material-symbols-outlined text-lg transition-transform ${showAllTransformations ? "rotate-180" : ""}`}
                  >
                    expand_more
                  </span>
                </button>
              </div>
            )}

            <h4 className="text-xs font-bold uppercase tracking-widest text-slate-400 dark:text-gray-500 mb-6 text-center">
              More Success Stories
            </h4>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-8">
              {testimonials
                .slice(0, showAllTestimonials ? undefined : 6)
                .map((story, i) => (
                  <div
                    key={i}
                    className="bg-background-light dark:bg-surface-dark p-3 rounded-xl border border-gray-200 dark:border-gray-700 flex flex-col h-full shadow-sm hover:shadow-md transition-shadow"
                  >
                    <div className="flex text-yellow-500 text-[10px] mb-1">
                      {[...Array(5)].map((_, starIndex) => {
                        const rating = story.rating || 5;
                        let icon = "star_outline";
                        let isFilled = false;

                        if (starIndex < Math.floor(rating)) {
                          icon = "star";
                          isFilled = true;
                        } else if (starIndex < rating) {
                          icon = "star_half";
                          isFilled = true;
                        }

                        return (
                          <span
                            key={starIndex}
                            className={`material-symbols-outlined text-[10px] ${isFilled ? "[font-variation-settings:'FILL'_1]" : ""}`}
                          >
                            {icon}
                          </span>
                        );
                      })}
                    </div>
                    <p className="text-[11px] text-slate-600 dark:text-gray-300 line-clamp-4 flex-grow italic">
                      &quot;{story.quote}&quot;
                    </p>
                    <p className="text-[10px] text-slate-400 mt-2 font-bold shrink-0">
                      - {story.author}
                    </p>
                  </div>
                ))}
            </div>

            {testimonials.length > 6 && (
              <div className="flex justify-center mb-8">
                <button
                  onClick={handleTestimonialsToggle}
                  className="text-sm font-bold text-primary hover:text-primary-dark transition-colors flex items-center gap-1"
                >
                  {showAllTestimonials ? "See Less" : "See More Stories"}
                  <span
                    className={`material-symbols-outlined text-lg transition-transform ${showAllTestimonials ? "rotate-180" : ""}`}
                  >
                    expand_more
                  </span>
                </button>
              </div>
            )}

            <div className="mt-8 text-center bg-white dark:bg-gray-800/50 p-8 rounded-2xl max-w-2xl mx-auto border border-gray-100 dark:border-gray-700 shadow-xl relative overflow-hidden group">
              <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-transparent via-primary to-transparent opacity-50"></div>
              <h4 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
                Claim Your New Life
              </h4>
              <p className="text-slate-500 dark:text-gray-400 mb-6 text-sm max-w-sm mx-auto">
                Don't let another year pass you by. The time to act is now.
              </p>

              <div className="flex justify-center">
                <div className="w-full md:w-auto md:min-w-[320px]">
                  <div className="relative group">
                    <div className="absolute -inset-1 bg-gradient-to-r from-primary to-blue-600 rounded-xl blur-xl opacity-30 group-hover:opacity-60 transition duration-500"></div>
                    <button
                      onClick={() => handleCtaClick("mid")}
                      className="relative w-full flex flex-col items-center justify-center bg-gradient-to-r from-primary to-blue-600 hover:to-primary-dark transition-all duration-200 rounded-xl py-4 px-10 shadow-lg shadow-primary/25 mx-auto overflow-hidden"
                    >
                      <div className="absolute top-0 -left-full w-full h-full bg-gradient-to-r from-transparent via-white/20 to-transparent skew-x-[-30deg] animate-[shimmer_2.5s_infinite]"></div>
                      <div className="flex items-center gap-2 text-white text-lg font-bold leading-none tracking-wide z-10">
                        <span>Start Your Transformation</span>
                        <span className="material-symbols-outlined text-xl">
                          arrow_forward
                        </span>
                      </div>
                    </button>
                  </div>
                </div>
              </div>
              <p className="text-center text-[11px] text-slate-500 dark:text-gray-400 mt-4 font-medium px-4 leading-tight opacity-70">
                {buttonSubText}
              </p>
            </div>
          </div>
        </section>

        <section className="px-5 py-12 max-w-4xl mx-auto w-full">
          <h3 className="text-2xl font-bold text-center text-slate-900 dark:text-white mb-8">
            Frequently Asked Questions
          </h3>
          <div className="space-y-4">
            {[
              {
                q: "Why is the 12-Week Challenge mandatory?",
                a: "We don't do \"maintenance\" coaching for new clients. The 12-Week Challenge is our system's filter. It's designed to install the necessary discipline, habits, and physical foundation required for long-term transformation. Every client must prove they can follow the system before we consider long-term partnership.",
              },
              {
                q: "What happens after the 12 weeks are up?",
                a: "Once you complete the challenge, you have two options: move into our graduate-level performance coaching for life-long maintenance, or take the blueprint and continue on your own with the body you've built.",
              },
              {
                q: "Who is this program NOT for?",
                a: 'This is not for men looking for a "quick fix" or those who aren\'t ready to be held accountable. If you are unwilling to log your food, check-in daily, or follow a structured plan, this will not work. We filter for men who are done with excuses.',
              },
            ].map((faq, i) => (
              <div
                key={i}
                className="group border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden"
              >
                <details
                  className="bg-background-light dark:bg-surface-dark"
                  onToggle={(event) => {
                    if (event.currentTarget.open) {
                      trackPixelEvent("faq_open", { question: faq.q });
                    }
                  }}
                >
                  <summary className="flex items-center justify-between p-4 cursor-pointer list-none text-slate-900 dark:text-white font-semibold select-none">
                    <span>{faq.q}</span>
                    <span className="material-symbols-outlined transition-transform group-open:rotate-180">
                      expand_more
                    </span>
                  </summary>
                  <div className="px-4 pb-4 text-sm text-slate-600 dark:text-gray-300 leading-relaxed border-t border-gray-100 dark:border-gray-700/50 pt-3">
                    {faq.a}
                  </div>
                </details>
              </div>
            ))}
          </div>
        </section>

        <section className="pb-12 pt-6 px-5 bg-background-light dark:bg-background-dark border-t border-gray-200 dark:border-gray-800">
          <div className="text-center mb-8">
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-2">
              Stop Starting Over.
            </h2>
            <p className="text-slate-500 dark:text-gray-400 text-sm">
              Make this the last time you ever have to &quot;get back in
              shape.&quot;
            </p>
          </div>
          <div className="max-w-md mx-auto flex flex-col items-center">
            <div className="w-full">
              <div className="relative group">
                <div className="absolute -inset-1 bg-gradient-to-r from-primary to-blue-600 rounded-xl blur-xl opacity-25 group-hover:opacity-50 transition duration-500"></div>
                <button
                  onClick={() => handleCtaClick("final")}
                  className="relative w-full flex flex-col items-center justify-center bg-gradient-to-r from-primary to-blue-600 hover:to-primary-dark transition-all duration-200 rounded-xl py-4 px-10 shadow-lg shadow-primary/25 mx-auto overflow-hidden"
                >
                  <div className="absolute top-0 -left-full w-full h-full bg-gradient-to-r from-transparent via-white/20 to-transparent skew-x-[-30deg] animate-[shimmer_2.5s_infinite]"></div>
                  <div className="flex items-center gap-2 text-white text-lg font-bold leading-none tracking-wide z-10">
                    <span>Start Your Transformation</span>
                    <span className="material-symbols-outlined text-xl">
                      arrow_forward
                    </span>
                  </div>
                </button>
              </div>
            </div>
            <p className="text-center text-[11px] text-slate-500 dark:text-gray-400 mb-10 mt-4 font-medium px-4 leading-tight opacity-70">
              {buttonSubText}
            </p>
          </div>
          <div className="flex justify-center gap-6 text-xs text-slate-400 dark:text-gray-600 mb-8">
            <a
              className="hover:text-primary transition-colors"
              href="/privacy-policy"
            >
              Privacy Policy
            </a>
            <a
              className="hover:text-primary transition-colors"
              href="/terms-of-service"
            >
              Terms of Service
            </a>
            <a
              className="hover:text-primary transition-colors"
              href="mailto:olympiaacademy@chibsolympia.com"
              onClick={() => trackPixelEvent("footer_contact_click")}
            >
              Contact
            </a>
          </div>
          <div className="flex items-center justify-center gap-2 opacity-50">
            <span className="material-symbols-outlined text-slate-400 text-xl">
              fitness_center
            </span>
            <span className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">
              Olympia Academy Coaching © {new Date().getFullYear()}
            </span>
          </div>
        </section>

        {/* Video Modal removed - videos play in place */}
      </div>
    </div>
  );
};

export default TwelveWeekChallenge;
